import React, { memo, useCallback, useState, useEffect, useMemo } from "react";
import GPSelectMultiple from '@gp/components/SelectMultiple';
import GPSelect from '@gp/components/Select';
import DateSelector from "./components/DateSelector";
import {
  getArraySort,
} from '@gp/utils/common';
import {
  fetchStudioOverallOverviewPageData,
} from '@gp/pages/StudioDashboard/StudioOverall/services/studioOverall';
import { useRequest, useDebounce } from 'ahooks';
import './index.scss';

interface FilterPropsType {
  loading: boolean;
  dateRange: string;
  onChange: ((
    bo: string[],
    studio: string[],
    category: string[],
    dateType: dateTypes,
    date: string[],
  ) => void) | null;
}

const DEFAULT_PROPS: FilterPropsType = {
  loading: true,
  dateRange: '',
  onChange: null,
};

function Filter(props: FilterPropsType) {
  const {
    loading,
    dateRange,
    onChange,
  } = props;

  const [category, setCategory] = useState<string[]>([]);
  const [dateType, setDateType] = useState<dateTypes>('quarter');
  const [date, setDate] = useState<string[]>([]);
  const debounceDate = useDebounce(date, { wait: 50 });
  const [selectBO, setSelectBO] = useState<string[]>([]);
  const [lastSelectBO, setLastSelectBO] = useState<string[]>([]);
  const [selectStudio, setSelectStudio] = useState<string[]>([]);
  const [lastSelectStudio, setLastSelectStudio] = useState<string[]>([]);

  const {
    loading: filterLoading,
    data: filterList,
  } = useRequest(
    fetchStudioOverallOverviewPageData,
    {defaultParams: [{page: 'calendar'}]},
  );

  const boOptions = useMemo(() => {
    const options = [];
    const boList = filterList?.management_team;
    if (Array.isArray(boList) && boList?.length) {
      // 先排序
      let isIncludeOthersItem = false;
      const filterOthersBOlist = boList?.filter((t: string) => {
        if (t?.toLowerCase() === 'others') {
          isIncludeOthersItem = true;
        }
        return t?.toLowerCase() !== 'others';
      });
      const tempSortBOList = getArraySort(filterOthersBOlist, 'string', 'asc');
      const sortBOList = isIncludeOthersItem ? [...tempSortBOList, 'Others'] : tempSortBOList;
      for(let i = 0;i < sortBOList?.length;i ++) {
        const t = sortBOList?.[i];
        options.push({
          text: t,
          value: t,
          groupKey: 'separated',
        });
      }
    }

    return options;
  }, [filterLoading]);

  const studioOptions = useMemo(() => {
    const studioList = filterList?.studio;
    if (!studioList || !Array.isArray(studioList)) {
      return [];
    }
    const _options = getArraySort(studioList, 'string', 'asc', 'name')?.map((t: {name: string; id: string;}) => ({
      value: t?.id || '',
      text: t?.name || '-',
      label: t?.name || '-',
      groupKey: 'separated',
    })) || [];

    return _options;
  }, [filterLoading]);

  const handleBOFilterChange = useCallback((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === filterList?.management_team?.length && !lastSelectBO?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === filterList?.management_team?.length) {
      setSelectBO([]);
      setLastSelectBO([]);
      onChange?.(
        [],
        selectStudio,
        category,
        dateType,
        debounceDate,
      );
      return;
    }

    const newSelectBO = v ? [...v] : [];
    setSelectBO(newSelectBO);
    setLastSelectBO(newSelectBO);
    onChange?.(
      newSelectBO,
      selectStudio,
      category,
      dateType,
      debounceDate,
    );
  }, [filterList?.management_team?.length, lastSelectBO, selectStudio, category, dateType, debounceDate]);

  const handleStudioFilterChange = useCallback((v: string[]) => {
    // 重复全选的效果不请求接口
    if (v?.length === filterList?.studio?.length && !lastSelectStudio?.length) return;
    // 当length是全选时，也设置成[]
    if (v?.length === filterList?.studio?.length) {
      setSelectStudio([]);
      setLastSelectStudio([]);
      onChange?.(
        selectBO,
        [],
        category,
        dateType,
        debounceDate,
      );
      return;
    }

    const newSelectStudio = v ? [...v] : [];
    setSelectStudio(newSelectStudio);
    setLastSelectStudio(newSelectStudio);
    onChange?.(
      selectBO,
      newSelectStudio,
      category,
      dateType,
      debounceDate,
    );
  }, [filterList?.studio?.length, lastSelectStudio, selectBO, category, dateType, debounceDate]);

  // 用于是月筛选器时不要输入聚焦
  const handleMonthFocus = useCallback((e) => { e.target.blur() }, []);
  // 用于是年筛选器时不要输入聚焦
  const handleYearFocus = useCallback((e) => { e.target.blur() }, []);

  const categoryHandleChange = useCallback(
    (v: string[]) => {
      // 当前后都是全选时规避重复请求接口
      if ([0, 2]?.includes(category?.length) && [0, 2]?.includes(v?.length)) return;
      setCategory(v);
    },
    [category],
  );

  const dateTypeHandleChange = useCallback(
    (v: dateTypes) => {
      setDateType(v);
      // 解除事件绑定
      const monthEl = document.querySelectorAll('.calendar-filter-date-month-selector .tea-input');
      const yearEl = document.querySelectorAll('.calendar-filter-date-year-selector .tea-input');
      if (monthEl) {
        for (let i = 0;i < monthEl?.length;i ++) {
          const t = monthEl?.[i];
          if (t) {
            t.removeEventListener('focus', handleMonthFocus);
          }
        }
      }
      if (yearEl) {
        for (let i = 0;i < yearEl?.length;i ++) {
          const t = yearEl?.[i];
          if (t) {
            t.removeEventListener('focus', handleYearFocus);
          }
        }
      }
    },
    [],
  );

  const dateHandleChange = useCallback(
    (v: string[], type: dateTypes) => {
      setDate([...v]);
    },
    [],
  );

  // 触发Change
  useEffect(() => {
    if (!Array.isArray(category) || !dateType || !Array.isArray(debounceDate) || !debounceDate?.length) return;
    onChange?.(
      selectBO,
      selectStudio,
      category,
      dateType,
      debounceDate,
    );
  }, [category, debounceDate]);

  return (
    <section className="dbg-studio-overall-calendar-filter-content">
      <GPSelectMultiple
        className="calendar-filter-bo-selector"
        style={{marginRight: 10}}
        label="Business Owner"
        searchable
        disabled={filterLoading || !filterList?.management_team || !filterList?.management_team?.length}
        options={boOptions}
        value={selectBO}
        onChange={handleBOFilterChange}
      />
      <GPSelectMultiple
        className="calendar-filter-studio-selector"
        style={{marginRight: 10}}
        label="Studio"
        listWidth={260}
        matchButtonWidth={false}
        searchable
        disabled={filterLoading || !filterList?.studio || !filterList?.studio?.length}
        options={studioOptions}
        value={selectStudio}
        onChange={handleStudioFilterChange}
      />
      <GPSelectMultiple
        className="calendar-filter-category-selector"
        style={{marginRight: 10}}
        label="Category"
        disabled={loading || !dateRange}
        options={[
          {
            value: '1',
            text: 'Project Milestone',
            groupKey: 'separated',
          },
          {
            value: '2',
            text: 'Management',
            groupKey: 'separated',
          },
        ]}
        value={category}
        onChange={categoryHandleChange}
      />
      <div className="calendar-filter-date-selector">
        <GPSelect
          className="calendar-filter-date-type-selector"
          style={{width: 100}}
          disabled={loading || !dateRange}
          label=""
          value={dateType}
          options={[
            {
              value: 'month',
              text: 'Month',
            },
            {
              value: 'quarter',
              text: 'Quarter',
            },
            {
              value: 'year',
              text: 'Year',
            },
          ]}
          onChange={dateTypeHandleChange}
        />
        <DateSelector
          loading={loading}
          range={dateRange}
          dateType={dateType}
          monthOnFocus={handleMonthFocus}
          yearOnFocus={handleYearFocus}
          onChange={dateHandleChange}
        />
      </div>
    </section>
  );
}

Filter.defaultProps = DEFAULT_PROPS;

export default memo(Filter);
